import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { IconBell, IconCap } from './icons';

export function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <div className={`brand ${compact ? 'compact' : ''}`}>
      <span className="brand-mark">
        <IconCap />
      </span>
      {!compact && <span className="brand-name">Exam Tablet Management</span>}
    </div>
  );
}

export function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'AD';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0]}${parts[parts.length - 1]![0]}`.toUpperCase();
}

/**
 * The bar above the workspace: who is signed in, and alerts.
 *
 * There is deliberately no global search here. Every page that holds a list
 * already has a search box scoped to that list, and a second box in the chrome
 * only raises the question of which one is in effect.
 */
export function Topbar({ adminName, role }: { adminName: string; role: string }) {
  return (
    <header className="topbar">
      <div className="topbar-end">
        <Link to="/notifications" className="icon-btn" aria-label="Notifications">
          <IconBell />
        </Link>
        <div className="who">
          <span className="avatar">{initials(adminName)}</span>
          <span className="who-text">
            <strong>{adminName}</strong>
            <span>{role}</span>
          </span>
        </div>
      </div>
    </header>
  );
}

export function MetricCard({
  tone,
  icon,
  label,
  value,
}: {
  tone: 'blue' | 'green' | 'red' | 'orange' | 'purple';
  icon: ReactNode;
  label: string;
  value: string | number;
}) {
  return (
    <div className={`metric metric-${tone}`}>
      <div className="metric-icon">{icon}</div>
      <div className="metric-label">{label}</div>
      <div className="metric-value">{value}</div>
      <svg className="metric-wave" viewBox="0 0 200 40" preserveAspectRatio="none" aria-hidden>
        <path d="M0 28 C40 8, 70 36, 110 18 C150 2, 170 30, 200 14 V40 H0 Z" />
      </svg>
    </div>
  );
}

export function KioskSwitch({ on }: { on: boolean | null }) {
  const active = on === true;
  return (
    <span className={`switch ${active ? 'on' : ''}`} aria-label={active ? 'Kiosk on' : 'Kiosk off'}>
      <span className="switch-knob" />
    </span>
  );
}

export function BatteryMeter({ level }: { level: number | null }) {
  if (level === null) return <span className="muted small">—</span>;
  const tone = level <= 15 ? 'danger' : level <= 30 ? 'warn' : 'ok';
  return (
    <span className="battery">
      <span className="battery-track">
        <span className={`battery-fill ${tone}`} style={{ width: `${Math.max(6, Math.min(100, level))}%` }} />
      </span>
      <span>{level}%</span>
    </span>
  );
}

export function Donut({
  online,
  offline,
}: {
  online: number;
  offline: number;
}) {
  const total = Math.max(1, online + offline);
  const r = 42;
  const c = 2 * Math.PI * r;
  const off = (offline / total) * c;
  const on = (online / total) * c;
  return (
    <svg className="donut" viewBox="0 0 120 120" aria-hidden>
      <circle cx="60" cy="60" r={r} fill="none" stroke="#e8eef7" strokeWidth="14" />
      <circle
        cx="60"
        cy="60"
        r={r}
        fill="none"
        stroke="#ef4444"
        strokeWidth="14"
        strokeDasharray={`${off} ${c - off}`}
        strokeDashoffset={c * 0.25}
        strokeLinecap="round"
      />
      {online > 0 && (
        <circle
          cx="60"
          cy="60"
          r={r}
          fill="none"
          stroke="#16a34a"
          strokeWidth="14"
          strokeDasharray={`${on} ${c - on}`}
          strokeDashoffset={c * 0.25 - off}
          strokeLinecap="round"
        />
      )}
      <text x="60" y="56" textAnchor="middle" className="donut-num">
        {offline}
      </text>
      <text x="60" y="74" textAnchor="middle" className="donut-cap">
        offline
      </text>
    </svg>
  );
}
